/**
 * 角色创建类型定义
 *
 * 定义角色创建流程中使用的数据结构Schema
 */

import { z } from 'zod';
import {
  normalizeBuildTemplateId,
  normalizeRaceId,
  normalizeOptionalBuildTemplateId,
  normalizeOptionalIdentityTemplateId,
} from '@/IsekaiRPG/modules/character/characterSemantic';
import { RankSchema } from '@/IsekaiRPG/shared/types';
import { AttributesSchema } from './characterEntity';
import { CharacterPresencePatchSchema } from './characterPatch';
import {
  CharacterActivityKindSchema,
  CharacterPresenceScopeSchema,
  CharacterSceneRoleSchema,
} from './characterPresence';
import {
  AppearanceSchema,
  CharacterProfileInputSchema,
  CoreTraitsSchema,
  FormativeEventSchema,
  GenderSchema,
} from './characterProfile';
import { BuildingTypeSchema } from '@/IsekaiRPG/modules/city/cityTypes';
import { AISkillPayloadSchema } from '@/IsekaiRPG/modules/skill/skillTypes';

// ============================================
// 创角表单数据
// ============================================

export const CreationGenderSchema = z.union([GenderSchema, z.literal('')]);
export type CreationGender = z.infer<typeof CreationGenderSchema>;

/**
 * 角色创建数据 Schema
 *
 * 这是创角流程中的临时表单数据
 * 创角完成后会转换为 CharacterProfile
 */
export const CharacterCreationDataSchema = z.object({
  /** 角色名称 */
  name: z.string().default(''),
  /** 年龄 */
  age: z.string().default(''),
  /** 性别（空字符串表示未选择） */
  gender: CreationGenderSchema.default(''),
  /** 实例层身份描述，不参与构筑计算 */
  identity: z.string().default(''),
  /** 角色背景 */
  background: z.string().default(''),
  /** 种族 ID（对应 data/races 中的 race id） */
  race: z.preprocess(value => normalizeRaceId(typeof value === 'string' ? value : undefined), z.string()).default(''),
  /** 构筑模板 ID */
  buildTemplateId: z.preprocess(
    value => normalizeBuildTemplateId(typeof value === 'string' ? value : undefined),
    z.string(),
  ).default(''),
  /** 出生国家名称（同时作为冒险起点） */
  state: z.string().default(''),
  /** 头像/肖像 */
  avatar: z.string().default(''),
});

export type CharacterCreationData = z.infer<typeof CharacterCreationDataSchema>;

// ============================================
// 创角步骤
// ============================================

/**
 * 创角步骤枚举
 */
export const CreationStep = z.enum(['race', 'build', 'state', 'info']);
export type CreationStepValue = z.infer<typeof CreationStep>;

/**
 * 步骤配置
 */
export interface StepConfig {
  step: number;
  id: CreationStepValue;
  title: string;
  description: string;
}

// ============================================
// AI 生成角色 Payload
// ============================================

/**
 * AI 角色骨架生成 Payload
 *
 * 设计原则：
 * - 只承载闭集机械分类，供系统先构建稳定骨架
 * - 细节补完、语义设定与数值修正由 revision patch 再补
 */
export const AICharacterSeedPayloadSchema = z.object({
  raceId: z.preprocess(value => normalizeRaceId(typeof value === 'string' ? value : undefined), z.string().min(1)),
  identityTemplateId: z
    .preprocess(
      value => normalizeOptionalIdentityTemplateId(typeof value === 'string' ? value : undefined),
      z.string().optional(),
    )
    .optional(),
  buildTemplateId: z
    .preprocess(
      value => normalizeOptionalBuildTemplateId(typeof value === 'string' ? value : undefined),
      z.string().optional(),
    )
    .optional(),
  tags: z.array(z.string()).default([]),
  rank: RankSchema.optional(),
}).strict();
export type AICharacterSeedPayload = z.infer<typeof AICharacterSeedPayloadSchema>;

/**
 * AI 角色蓝图生成 Payload
 *
 * 设计原则：
 * - 先直接成型完整人物设定 profile
 * - 机械信息只保留 profile 之外仍需要的字段
 */
const AICharacterBlueprintProfileSchema = CharacterProfileInputSchema.strict();

export const AICharacterBlueprintPayloadSchema = z
  .object({
    profile: AICharacterBlueprintProfileSchema,
    mechanics: z
      .object({
        identityTemplateId: z
          .preprocess(
            value => normalizeOptionalIdentityTemplateId(typeof value === 'string' ? value : undefined),
            z.string().optional(),
          )
          .optional(),
        buildTemplateId: z
          .preprocess(
            value => normalizeOptionalBuildTemplateId(typeof value === 'string' ? value : undefined),
            z.string().optional(),
          )
          .optional(),
        rank: RankSchema.optional(),
      })
      .default(() => ({})),
  })
  .strict();
export type AICharacterBlueprintPayload = z.infer<typeof AICharacterBlueprintPayloadSchema>;

/**
 * AI 战斗/技能设计 Payload
 *
 * 设计原则：
 * - 只关注属性偏态与技能设计
 * - 不再要求 AI 同时补 profile / presence
 */
export const AICharacterCombatDesignPayloadSchema = z
  .object({
    attributes: AttributesSchema.partial().optional(),
    skills: z.array(AISkillPayloadSchema).default([]),
    removeSkillIds: z.array(z.string()).default([]),
  })
  .strict();
export type AICharacterCombatDesignPayload = z.infer<typeof AICharacterCombatDesignPayloadSchema>;

/**
 * AI 世界调度设计 Payload
 *
 * 设计原则：
 * - 先输出轻量 presence hints，再由系统转成正式 presencePatch
 * - 避免直接让 AI 写完整 CharacterPresenceSchema
 */
export const AICharacterPresenceDesignPayloadSchema = z
  .object({
    homeBuildingType: BuildingTypeSchema.optional(),
    activityCenterBuildingType: BuildingTypeSchema.optional(),
    mobilityScope: CharacterPresenceScopeSchema.optional(),
    canLeaveCity: z.boolean().optional(),
    fixedResident: z.boolean().optional(),
    avoidNightTravel: z.boolean().optional(),
    avoidDangerousAreas: z.boolean().optional(),
    sceneDefaultRole: CharacterSceneRoleSchema.optional(),
    preferredActivities: z.array(CharacterActivityKindSchema).default([]),
    avoidedActivities: z.array(CharacterActivityKindSchema).default([]),
    preferredBuildingTypes: z.array(BuildingTypeSchema).default([]),
    avoidedBuildingTypes: z.array(BuildingTypeSchema).default([]),
  })
  .strict();
export type AICharacterPresenceDesignPayload = z.infer<typeof AICharacterPresenceDesignPayloadSchema>;

/**
 * AI 角色补完 / 修正 Payload
 */
const ProfilePatchInnerSchema = z.object({
  name: z.string().optional(),
  age: z.string().optional(),
  gender: GenderSchema.optional(),
  avatar: z.string().optional(),
  country: z.string().optional(),
  identity: z.string().optional(),
  background: z.string().optional(),
  combat_style: z.string().optional(),
  tags: z.array(z.string()).default([]),
  removeTags: z.array(z.string()).default([]),
  coreTraits: CoreTraitsSchema.optional(),
  formativeEvents: z.array(FormativeEventSchema).default([]),
  likes: z.array(z.string()).default([]),
  dislikes: z.array(z.string()).default([]),
  appearance: AppearanceSchema.partial().optional(),
}).strict();

const EntityPatchInnerSchema = z.object({
  attributes: AttributesSchema.partial().optional(),
  skills: z.array(AISkillPayloadSchema).default([]),
  removeSkillIds: z.array(z.string()).default([]),
}).strict();

export const AICharacterRevisionPayloadSchema = z.object({
  profilePatch: ProfilePatchInnerSchema.default(() => ProfilePatchInnerSchema.parse({})),
  entityPatch: EntityPatchInnerSchema.default(() => EntityPatchInnerSchema.parse({})),
  presencePatch: CharacterPresencePatchSchema.default(() => CharacterPresencePatchSchema.parse({})),
}).strict();
export type AICharacterRevisionPayload = z.infer<typeof AICharacterRevisionPayloadSchema>;
